import {useState, useRef, forwardRef, useMemo, useEffect, useCallback} from 'react';
import {convertToRoomString, splitRoomString} from '../../../../../shared/utils/room-string-utils';

import ArrowDownIcon from '../../../../Icons/ArrowDownIcon';
import GITickBox from '@stencil/gi-components/src/GITickBox/GITickBox';
import TravellerInfo from '../TravellerInfo/TravellerInfo';

import useChildAgePicker from '../../../../../shared/hooks/useChildAgePicker';
import useOutsideClick from '../../../../../shared/hooks/useOutsideClick';
import useMultiRoomConfig from '@stencil/hotels-hooks/src/useMultiRoomConfig';

//Styles
import * as S from './PaxWidget.styles';

enum CheckBoxState {
  Default_Not_Clicked = 0,
  User_Checked = 1,
  User_Unchecked = 2,
}

const PaxWidget = forwardRef(
  (
    props: {
      togglePaxWidget;
      roomString;
      updateQueryCaller;
      gaHandler;
      updateSearch;
      pushOmnitureTracking;
      maxChildAge;
      setCalendarMode;
      isDateRequired;
      setPaxRequired;
      onDestinationPaxDateChangeHandler;
      containerStyle;
      multiRoomConfig;
      appliedFiltersDispatch;
      appliedFiltersObj;
      isDetailPage;
    },
    ref,
  ) => {
    const {
      togglePaxWidget,
      roomString,
      updateQueryCaller,
      gaHandler,
      updateSearch,
      pushOmnitureTracking,
      maxChildAge,
      setCalendarMode = () => {},
      setPaxRequired = () => {},
      isDateRequired = false,
      onDestinationPaxDateChangeHandler = () => {},
      containerStyle = {},
      multiRoomConfig = {},
      appliedFiltersDispatch = () => {},
      appliedFiltersObj = () => {},
      isDetailPage = false,
    } = props;

    const {
      roomCount: roomCountInState,
      paxCount: paxCountInState,
      childCount: childCountInState,
      childAgeArray,
    } = splitRoomString(roomString);

    const [adultCount, setAdultCount] = useState(paxCountInState);
    const [roomCount, setRoomCount] = useState(roomCountInState);
    const [childCount, setChildCount] = useState(childCountInState);

    const [childAgeArrayInState, actionHandler, setChildAgeArrayInState] = useChildAgePicker({
      minLimit: 0,
      maxLimit: maxChildAge,
      defaultVal: childAgeArray,
    });

    const {multiRoomFilterApplyCaller, isMultiRoomFilterSelect, multiRoomFilterConfig} =
      useMultiRoomConfig({
        roomCount,
        adultCount,
        childCount,
        appliedFiltersObj,
        appliedFiltersDispatch,
        multiRoomConfig,
        isDonotShowMultiRoomFilter: !!isDetailPage,
      });
    /**
     * Manages the selection state of a multi-room filter.
     *
     * @remarks
     * The checkbox state can be:
     *  - 0: not clicked
     *  - 1: true (user checked)
     *  - 2: false (user unchecked)
     *
     * @param checkBoxState - Indicates the selection state
     * @param setCheckBoxState - Updates the checkbox state
     */
    const [checkBoxState, setCheckBoxState] = useState<number>(CheckBoxState.Default_Not_Clicked);
    const [isUpdateSearchCalled, setIsUpdateSearch] = useState<boolean>(false);

    const roomStringVal = convertToRoomString({
      roomCount,
      paxCount: adultCount,
      childCount,
      childAgeArray: childAgeArrayInState,
    });

    useEffect(() => {
      if (isMultiRoomFilterSelect) {
        setCheckBoxState(CheckBoxState.User_Checked);
      }
    }, [isMultiRoomFilterSelect]);

    useEffect(() => {
      if (isUpdateSearchCalled) {
        togglePaxWidget(false);
        if (!isDateRequired) {
          updateSearch();
          updateQueryCaller({isDatePickRequired: false});
          // event tracking
          if (pushOmnitureTracking) {
            pushOmnitureTracking(roomStringVal);
          }
        }
      }
    }, [isUpdateSearchCalled, roomStringVal, isDateRequired]);

    const doneClickHandler = useCallback(() => {
      if ((checkBoxState && !isMultiRoomFilterSelect) || checkBoxState === 2) {
        multiRoomFilterApplyCaller();
      }
      if (isDateRequired) {
        setCalendarMode('checkin');
      }

      setPaxRequired(false);

      updateQueryCaller({roomString: roomStringVal});
      setIsUpdateSearch(true);
    }, [
      /** Dependencies **/
      checkBoxState,
      multiRoomFilterApplyCaller,
      isMultiRoomFilterSelect,
      isDateRequired,
      setCalendarMode,
      setPaxRequired,
      convertToRoomString,
      roomCount,
      adultCount,
      childCount,
      childAgeArrayInState,
      updateQueryCaller,
      roomStringVal,
    ]);

    let disableViewStays = false;
    if (
      childAgeArrayInState &&
      Array.isArray(childAgeArrayInState) &&
      childAgeArrayInState.length &&
      childAgeArrayInState.includes(0)
    ) {
      disableViewStays = true;
    }

    return (
      <S.WrapperDiv ref={ref} data-testid="pax-modal-home" customStyle={containerStyle}>
        <S.ContentWrapperDiv>
          <S.ContentItemWrapperDiv>
            <S.ContentItemTextWrapperSpan>
              Rooms
              <S.ContentItemConditionTextWrapperSpan>(Max 8)</S.ContentItemConditionTextWrapperSpan>
            </S.ContentItemTextWrapperSpan>
            <S.ContentActionWrapperDiv>
              <S.ContentActionIconWrapperSpan
                data-testid="button-room-dec"
                onClick={() => {
                  if (roomCount === 1) {
                    alert('Minimum 1 is required');
                    return;
                  } else {
                    setRoomCount(roomCount - 1);
                  }
                }}
              >
                -
              </S.ContentActionIconWrapperSpan>
              <S.ContentActionValueWrapperSpan data-testid="room-count">
                {roomCount}
              </S.ContentActionValueWrapperSpan>

              <S.ContentActionIconWrapperSpan
                data-testid="button-room-add"
                onClick={() => {
                  if (roomCount > 7) {
                    alert('Max allowed upto 8');
                    return;
                  } else {
                    if (roomCount + 1 > adultCount) {
                      setAdultCount(adultCount + 1);
                    }
                    setRoomCount(roomCount + 1);
                  }
                }}
              >
                +
              </S.ContentActionIconWrapperSpan>
            </S.ContentActionWrapperDiv>
          </S.ContentItemWrapperDiv>
          <S.ContentItemWrapperDiv>
            <S.ContentItemTextWrapperSpan>
              Adults
              <S.ContentItemConditionTextWrapperSpan>
                ({maxChildAge}+ yr)
              </S.ContentItemConditionTextWrapperSpan>
            </S.ContentItemTextWrapperSpan>
            <S.ContentActionWrapperDiv>
              <S.ContentActionIconWrapperSpan
                data-testid="button-adult-dec"
                onClick={() => {
                  if (adultCount === 1) {
                    alert('Minimum 1 adult is required');
                    return;
                  }
                  if (adultCount - 1 < roomCount) setRoomCount(roomCount - 1);
                  setAdultCount(adultCount - 1);
                }}
              >
                -
              </S.ContentActionIconWrapperSpan>
              <S.ContentActionValueWrapperSpan data-testid="adult-count">
                {adultCount}
              </S.ContentActionValueWrapperSpan>
              <S.ContentActionIconWrapperSpan
                data-testid="button-adult-add"
                onClick={() => {
                  if (adultCount >= 16) {
                    alert('Max allowed upto 16');
                    return;
                  }
                  setAdultCount(adultCount + 1);
                }}
              >
                +
              </S.ContentActionIconWrapperSpan>
            </S.ContentActionWrapperDiv>
          </S.ContentItemWrapperDiv>
          <S.ContentItemWrapperDiv>
            <S.ContentItemTextWrapperSpan>
              Children
              <S.ContentItemConditionTextWrapperSpan>
                (0-{maxChildAge} yr)
              </S.ContentItemConditionTextWrapperSpan>
            </S.ContentItemTextWrapperSpan>
            <S.ContentActionWrapperDiv>
              <S.ContentActionIconWrapperSpan
                data-testid="button-child-dec"
                onClick={() => {
                  if (childCount < 1) {
                    alert('Not allowed');
                    return;
                  }
                  setChildCount(childCount - 1);
                  childAgeArrayInState.pop();
                  setChildAgeArrayInState(childAgeArrayInState);
                }}
              >
                -
              </S.ContentActionIconWrapperSpan>
              <S.ContentActionValueWrapperSpan data-testid="child-count">
                {childCount}
              </S.ContentActionValueWrapperSpan>
              <S.ContentActionIconWrapperSpan
                data-testid="button-child-add"
                onClick={() => {
                  let maxChildPerRoom = roomCount * 4 - 1;
                  // if (childCount > 8) {
                  if (childCount > maxChildPerRoom) {
                    // alert('Not allowed');
                    alert('Maximum 4 children per room are allowed');

                    return;
                  }
                  setChildCount(childCount + 1);
                  childAgeArrayInState.push(0);
                  setChildAgeArrayInState(childAgeArrayInState);
                }}
              >
                +
              </S.ContentActionIconWrapperSpan>
            </S.ContentActionWrapperDiv>
          </S.ContentItemWrapperDiv>

          {(childAgeArrayInState &&
            childAgeArrayInState.map((ageVal, index) => {
              return (
                <S.ContentItemWrapperDiv key={'childAge' + index + 1}>
                  <S.ContentItemTextWrapperSpan>
                    Child {index + 1}
                    <S.ContentItemConditionTextWrapperSpan>
                      Age
                    </S.ContentItemConditionTextWrapperSpan>
                  </S.ContentItemTextWrapperSpan>
                  <ChildAgePicker
                    val={childAgeArrayInState[index]}
                    actionHandler={actionHandler}
                    ageVal={ageVal}
                    index={index}
                    maxChildAge={maxChildAge}
                  />
                </S.ContentItemWrapperDiv>
              );
            })) ||
            null}

          <TravellerInfo />

          {multiRoomFilterConfig?.isShowMultiRoomFilter && (
            <GITickBox
              label={multiRoomFilterConfig?.multiRoomPaxString}
              onBoxClick={e => {
                e.stopPropagation();
                setCheckBoxState(prevValue => {
                  if (prevValue === CheckBoxState.User_Unchecked || !prevValue) {
                    return 1;
                  } else if (prevValue === CheckBoxState.User_Checked) {
                    return 2;
                  }
                  return CheckBoxState.Default_Not_Clicked;
                });
              }}
              isSelected={checkBoxState === 1}
              id="multiRoomConfig_tick"
              wrapperStyles={{padding: '0.2rem 1.6rem 1.6rem'}}
            />
          )}

          <S.ButtonWrapperDiv>
            <S.ButtonWrapper
              aria-label="button-pax-homeunif-dweb"
              data-testid="button-pax-homeunif-dweb"
              onClick={doneClickHandler}
              disabled={disableViewStays}
            >
              Done
            </S.ButtonWrapper>
          </S.ButtonWrapperDiv>
        </S.ContentWrapperDiv>
      </S.WrapperDiv>
    );
  },
);

export default PaxWidget;

function ChildAgePicker({actionHandler, index, ageVal, val, maxChildAge}) {
  const [toggleValue, toggleHandler] = useState(false);
  const childPickerRef = useRef();
  useOutsideClick(childPickerRef, () => {
    toggleHandler(false);
  });

  return (
    <>
      <S.ContentActionDropdownWrapper ref={childPickerRef} onClick={() => toggleHandler(true)}>
        <S.ContentActionValueWrapperSpan>
          {val === 0 ? 'Select' : val}
        </S.ContentActionValueWrapperSpan>
        <S.ContentActionIconWrapperSpan>
          <ArrowDownIcon />
        </S.ContentActionIconWrapperSpan>
      </S.ContentActionDropdownWrapper>
      {(toggleValue && (
        <S.ChildDropdownWrap>
          {[...Array(maxChildAge + 1)].map((_, arrIndex) => (
            <li
              onClick={e => {
                e.stopPropagation();
                actionHandler(index, arrIndex);
                toggleHandler(false);
              }}
            >
              {arrIndex === 0 ? 'Select' : arrIndex.toString()}
            </li>
          ))}
        </S.ChildDropdownWrap>
      )) ||
        null}
      {val === 0 ? (
        <S.SelectedChildAgeErrorText>Select child {index + 1} age</S.SelectedChildAgeErrorText>
      ) : null}
    </>
  );
}
